import { Component, OnInit, ViewChild, AfterViewInit } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { PageEvent } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { FormControl } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { ApiService } from "../../services/api.service";
import { CsvExportService } from "../../services/csv-export.service";

export interface FalloutRecord extends Record<string, unknown> {
  id: string;
  migration_id: number;
  source_entity: string;
  batch_id: string;
  record_id: string;
  error_category: string;
  error_message: string;
  record_snapshot: unknown;
  created_at: string;
  company_id?: number;
  company_name?: string;
  protocol_id?: string;
  sponsor_protocol_id?: string;
  siteid?: string;
}

@Component({
  selector: "app-fallout-tracker",
  templateUrl: "./fallout-tracker.component.html",
  styleUrls: ["./fallout-tracker.component.scss"],
  standalone: false,
})
export class FalloutTrackerComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    "migration_id",
    "company_name",
    "protocol_id",
    "sponsor_protocol_id",
    "siteid",
    "error_category",
    "error_message",
    "created_at",
  ];

  dataSource = new MatTableDataSource<FalloutRecord>();
  allRecords: FalloutRecord[] = [];
  migrationIdControl = new FormControl("");
  isLoading = false;
  
  // Paginator properties
  pageSize = 10;
  pageIndex = 0;
  totalRecords = 0;

  readonly errorCategoryMap: { [key: string]: string } = {
    foreign_key: "DATA_REF_INTEGRITY",
    foreign_key_violation: "DATA_REF_INTEGRITY",
    unique_violation: "DATA_DUPLICATE_KEY",
    duplicate_key: "DATA_DUPLICATE_KEY",
    not_null_violation: "MISSING_REQUIRED_DATA",
    check_violation: "DATA_VALIDATION_ERROR",
    data_error: "DATA_ERROR",
    string_data_right_truncation: "DATA_TOO_LONG",
    numeric_value_out_of_range: "DATA_OUT_OF_RANGE",
    invalid_text_representation: "INVALID_DATA_FORMAT",
    datetime_field_overflow: "INVALID_DATE_TIME",
  };

  readonly errorBadgeClassMap: { [key: string]: string } = {
    duplicate_key: "error-badge error-badge-validation",
    unique_violation: "error-badge error-badge-validation",
    foreign_key: "error-badge error-badge-constraint",
    foreign_key_violation: "error-badge error-badge-constraint",
    data_error: "error-badge error-badge-database",
  };

  readonly defaultBadgeClass = "error-badge error-badge-default";

  readonly columnHeaderMap: { [key: string]: string } = {
    migration_id: "MIGRATION ID",
    company_name: "COMPANY NAME",
    protocol_id: "PROTOCOL ID",
    sponsor_protocol_id: "SPONSOR PROTOCOL ID",
    siteid: "SITE ID",
    error_category: "ERROR CATEGORY",
    error_message: "ERROR MESSAGE",
    created_at: "CREATED AT",
  };

  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private apiService: ApiService,
    private csvExportService: CsvExportService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params["migrationId"]) {
        this.migrationIdControl.setValue(params["migrationId"]);
      }
    });

    this.migrationIdControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.applyFilters());
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    
    if (this.migrationIdControl.value) {
      this.loadFalloutData();
    }
  }

  handlePageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.applyPagination();
  }

  private applyPagination(): void {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.dataSource.data = this.allRecords.slice(startIndex, endIndex);
  }  loadFalloutData(): void {
    this.isLoading = true;
    const migrationId = this.migrationIdControl.value
      ? Number(this.migrationIdControl.value)
      : undefined;

    this.apiService.getFalloutRecords(undefined, migrationId).subscribe({
      next: (data) => {
        console.log("Fallout records received:", data);
        const records: FalloutRecord[] = Array.isArray(data) ? data : [];
        console.log("Processed records:", records, "Count:", records.length);

        this.allRecords = records;
        this.totalRecords = records.length;
        this.pageIndex = 0;
        this.applyPagination();
        this.isLoading = false;
      },
      error: (error) => {
        const errorMessage = error?.message || "Unknown error occurred";
        console.error("Error loading fallout records:", errorMessage, error);

        this.allRecords = [];
        this.totalRecords = 0;
        this.pageIndex = 0;
        this.dataSource.data = [];
        this.isLoading = false;
      },
    });
  }

  applyFilters(): void {
    this.loadFalloutData();
  }

  clearFilters(): void {
    this.migrationIdControl.setValue("");
    this.allRecords = [];
    this.totalRecords = 0;
    this.pageIndex = 0;
    this.dataSource.data = [];
  }

  exportToCSV(): void {
    const data = this.allRecords;
    if (data.length === 0) {
      console.warn("No data to export");
      return;
    }

    const transformedData = data.map((record) => {
      const mappedCategory = this.errorCategoryMap[record.error_category] || record.error_category;
      
      return {
        ...record,
        error_category: mappedCategory.toUpperCase(),
      };
    });

    // Map column names to custom headers
    const customHeaders = this.displayedColumns.map(col => this.columnHeaderMap[col] || col.toUpperCase());

    const filename = `fallout-records-${new Date().getTime()}`;

    this.csvExportService.exportToCsv(filename, transformedData, this.displayedColumns, customHeaders);
  }
}
